import React, { useState, useEffect, useRef } from 'react';
import Chat from './Chat';
import LyricsComponent from './LyricsComponent';
import StyleDescription from './StyleDescription';
import AdvancedOptions from './AdvancedOptions';
import CustomActions from './CustomActions';
import AdditionalOptions from './AdditionalOptions';
import Footer from '../layout/Footer';
import CreateAudioPlayer from '../ui/CreateAudioPlayer';
import TrimAudioModal from '../modals/TrimAudioModal';
import UploadingModal from '../modals/UploadingModal';
import ZeroCreditsBanner from '../ui/ZeroCreditsBanner';

const CreateForm = ({ onCreateSong, credits, isCreating = false, coverSongData, onCoverSongDataUsed, karaokeSongData, onKaraokeSongDataUsed, extendSongData, onExtendSongDataUsed, replaceSectionData, onReplaceSectionDataUsed, reuseLyricsData, onReuseLyricsDataUsed, introSongData, onIntroSongDataUsed, speedSongData, onSpeedSongDataUsed, homeToCreateData, onHomeToCreateDataUsed, hasPlaybar = false, selectedMode, onModeChange, selectedVersion, onVersionChange, onPlaySong, onSelectSong, selectedSongTitle, selectedSongForForm, onCreateWorkspace, onOpenWorkspace, selectedWorkspaceId, workspaces = [], workspaceChatHistory = null, onOpenLyricsEditor, onOpenStylesEditor, onRegisterSendMessage }) => {
  const [songDescription, setSongDescription] = useState("");
  const [styleDescription, setStyleDescription] = useState("");
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [styleGenres, setStyleGenres] = useState([]);
  const [isSongDescriptionSaved, setIsSongDescriptionSaved] = useState(false);
  const [isStyleDescriptionSaved, setIsStyleDescriptionSaved] = useState(false);
  const [savedDescriptions, setSavedDescriptions] = useState([]);
  const [savedStyleDescriptions, setSavedStyleDescriptions] = useState([]);
  const [songTitle, setSongTitle] = useState("");
  const [lyrics, setLyrics] = useState("");
  const [showCoverSong, setShowCoverSong] = useState(false);
  const [showTrimModal, setShowTrimModal] = useState(false);
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [trimmedAudioData, setTrimmedAudioData] = useState(null);
  const [showUploadingModal, setShowUploadingModal] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState("");
  const [selectedUploadOption, setSelectedUploadOption] = useState("Cover");
  const [audioUploadState, setAudioUploadState] = useState("normal");
  const [audioUploadProgress, setAudioUploadProgress] = useState(0);
  const [audioUploadRemainingTime, setAudioUploadRemainingTime] = useState(0);
  const uploadStartTimeRef = useRef(null);
  const [isExtendMode, setIsExtendMode] = useState(false);
  const [isReplaceSectionMode, setIsReplaceSectionMode] = useState(false);
  const [isIntroMode, setIsIntroMode] = useState(false);
  const [isCoverMode, setIsCoverMode] = useState(false);
  const [isFromWorkspace, setIsFromWorkspace] = useState(false);
  const [isMumbleMode, setIsMumbleMode] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);
  const [hasLyrics, setHasLyrics] = useState(false);
  const [isInstrumental, setIsInstrumental] = useState(false);
  const [isSpeedMode, setIsSpeedMode] = useState(false);
  const [isSpeedModeFromWorkspace, setIsSpeedModeFromWorkspace] = useState(false);
  const [isKaraokeMode, setIsKaraokeMode] = useState(false);
  const [isKaraokeModeFromWorkspace, setIsKaraokeModeFromWorkspace] = useState(false);
  const [introLength, setIntroLength] = useState(3);

  // Chat state - persisted across mode switches
  const [chatMessages, setChatMessages] = useState([]);
  const [hasMessages, setHasMessages] = useState(false);
  const [playingSongIndex, setPlayingSongIndex] = useState(null);
  const [selectedSongIndex, setSelectedSongIndex] = useState(null);

  // Load workspace chat history when workspace is selected
  useEffect(() => {
    if (selectedWorkspaceId && workspaceChatHistory && workspaceChatHistory.length > 0) {
      setChatMessages(workspaceChatHistory);
      setHasMessages(true);
    } else if (!selectedWorkspaceId) {
      // Reset to empty state when no workspace is selected
      setChatMessages([]);
      setHasMessages(false);
    }
  }, [selectedWorkspaceId, workspaceChatHistory]);

  // Handler for when a song is selected in chat mode
  const handleSongSelected = (songData) => {
    if (!songData) {
      // Deselecting - clear the form fields
      setSongTitle('');
      setLyrics('');
      setStyleDescription('');
      return;
    }

    // Populate form fields with selected song data
    setSongTitle(songData.title);
    setLyrics(songData.lyrics || '');

    // Set genres as text in the style description
    if (songData.genres) {
      setStyleDescription(songData.genres);
    }
  };

  // Watch for song selection from workspace and populate form
  useEffect(() => {
    if (selectedSongForForm) {
      // Populate form with selected song from workspace (works in both Chat and Custom mode)
      handleSongSelected({
        title: selectedSongForForm.title,
        genres: selectedSongForForm.genres,
        lyrics: selectedSongForForm.lyrics || '', // Use lyrics if available (from Chat), empty otherwise (from Workspace)
        image: selectedSongForForm.image
      });
    } else if (!selectedSongForForm && selectedMode === 'Custom' && chatMessages.length > 0) {
      // If no song selected but in Custom mode with chat history, pre-fill from latest lyrics message
      const lyricsMessage = chatMessages.find(msg => msg.type === 'lyrics');
      if (lyricsMessage) {
        setLyrics(lyricsMessage.lyrics || '');
        setStyleDescription(lyricsMessage.styles || '');
      }
    } else if (!selectedSongForForm) {
      // Clear form when song is deselected and no chat history
      handleSongSelected(null);
    }
  }, [selectedSongForForm, selectedMode, chatMessages]);

  // Handle cover song data
  useEffect(() => {
    if (coverSongData) {
      // Create a mock audio file URL for the cover song
      const mockAudioUrl = "/audio.mp3"; // You could use a real URL based on the song data
      
      // Reset all form data and set up cover mode in one batch
      setShowCoverSong(true);
      setSelectedAudioFile(null);
      setIsExtendMode(false);
      setIsReplaceSectionMode(false);
      setIsIntroMode(false);
      setIsCoverMode(true);
      setIsFromWorkspace(true);
      onModeChange?.("Custom");
      setTrimmedAudioData({
        fileName: coverSongData.title,
        audioUrl: mockAudioUrl,
        image: coverSongData.image,
        trimStart: 0,
        trimEnd: 1,
        originalDuration: parseFloat(coverSongData.duration.replace(':', '.'))
      });
      
      // Populate lyrics with generic content
      setLyrics(`[Verse 1]
I'm reaching for the stars tonight
Dancing in the neon light
Every moment feels so right
When you're here beside me

[Chorus]
We're living in this moment now
Nothing else can bring us down
Turn the music up so loud
Let the world just fade away

[Verse 2]
Colors painting through the sky
Time just seems to pass us by
Never need to question why
This feeling's getting stronger`);
      
      // Keep style description empty for cover mode
      setStyleDescription("");
      
      // Clear selected genres for suggestion display
      setSelectedGenres([]);
      
      // Set song title
      setSongTitle(`${coverSongData.title} (Cover)`);
      
      // Mark data as used
      onCoverSongDataUsed?.();
    }
  }, [coverSongData, onCoverSongDataUsed]);

  // Handle karaoke song data
  useEffect(() => {
    if (karaokeSongData) {
      console.log('CreateForm: Karaoke song data received:', karaokeSongData);
      // Create a mock audio file URL for the karaoke song
      const mockAudioUrl = "/audio.mp3"; // You could use a real URL based on the song data
      
      // Reset all form data and set up karaoke mode in one batch
      setShowCoverSong(true);
      setSelectedAudioFile(null);
      setIsExtendMode(false);
      setIsReplaceSectionMode(false);
      setIsIntroMode(false);
      setIsCoverMode(false);
      setIsFromWorkspace(true);
      setIsKaraokeModeFromWorkspace(true);
      onModeChange?.("Custom");
      console.log('CreateForm: Set isKaraokeModeFromWorkspace to true');
      setTrimmedAudioData({
        fileName: karaokeSongData.title,
        audioUrl: mockAudioUrl,
        image: karaokeSongData.image,
        trimStart: 0,
        trimEnd: 1,
        originalDuration: parseFloat(karaokeSongData.duration.replace(':', '.'))
      });
      
      // Clear lyrics for karaoke mode (user will sing their own)
      setLyrics("");
      
      // Keep style description empty for karaoke mode
      setStyleDescription("");
      
      // Clear selected genres for suggestion display
      setSelectedGenres([]);
      
      // Set song title
      setSongTitle(`${karaokeSongData.title} (Karaoke)`);
      
      // Mark data as used
      onKaraokeSongDataUsed?.();
    }
  }, [karaokeSongData, onKaraokeSongDataUsed]);


  // Handle extend song data
  useEffect(() => {
    if (extendSongData) {
      // Create a mock audio file URL for the cover song
      const mockAudioUrl = "/audio.mp3"; // You could use a real URL based on the song data
      
      // Reset all form data and set up extend mode in one batch
      setShowCoverSong(true);
      setSelectedAudioFile(null);
      setIsExtendMode(true);
      setIsReplaceSectionMode(false);
      setIsIntroMode(false);
      setIsCoverMode(false);
      setIsFromWorkspace(true);
      onModeChange?.("Custom");
      setTrimmedAudioData({
        fileName: extendSongData.title,
        audioUrl: mockAudioUrl,
        image: extendSongData.image,
        trimStart: 0,
        trimEnd: 1,
        originalDuration: parseFloat(extendSongData.duration.replace(':', '.'))
      });
      
      // Populate lyrics with generic content
      setLyrics(`[Verse 1]
I'm reaching for the stars tonight
Dancing in the neon light
Every moment feels so right
When you're here beside me

[Chorus]
We're living in this moment now
Nothing else can bring us down
Turn the music up so loud
Let the world just fade away

[Verse 2]
Colors painting through the sky
Time just seems to pass us by
Never need to question why
This feeling's getting stronger`);
      
      // Populate style description with the original song's genres for Custom mode
      setStyleDescription(extendSongData.genres);
      
      // Clear selected genres for suggestion display
      setSelectedGenres([]);
      
      // Set song title
      setSongTitle(`${extendSongData.title} (Extended)`);
      
      // Mark data as used
      onExtendSongDataUsed?.();
    }
  }, [extendSongData, onExtendSongDataUsed]);

  // Handle replace section song data
  useEffect(() => {
    if (replaceSectionData) {
      // Create a mock audio file URL for the cover song
      const mockAudioUrl = "/audio.mp3"; // You could use a real URL based on the song data
      
      // Reset all form data and set up replace section mode in one batch
      setShowCoverSong(true);
      setSelectedAudioFile(null);
      setIsExtendMode(false);
      setIsReplaceSectionMode(true);
      setIsIntroMode(false);
      setIsCoverMode(false);
      setIsFromWorkspace(true);
      onModeChange?.("Custom");
      setTrimmedAudioData({
        fileName: replaceSectionData.title,
        audioUrl: mockAudioUrl,
        image: replaceSectionData.image,
        trimStart: 0,
        trimEnd: 1,
        originalDuration: parseFloat(replaceSectionData.duration.replace(':', '.'))
      });
      
      // Populate lyrics with generic content
      setLyrics(`[Verse 1]
I'm reaching for the stars tonight
Dancing in the neon light
Every moment feels so right
When you're here beside me

[Chorus]
We're living in this moment now
Nothing else can bring us down
Turn the music up so loud
Let the world just fade away

[Verse 2]
Colors painting through the sky
Time just seems to pass us by
Never need to question why
This feeling's getting stronger`);
      
      // Populate style description with the original song's genres for Custom mode
      setStyleDescription(replaceSectionData.genres);
      
      // Clear selected genres for suggestion display
      setSelectedGenres([]);
      
      // Set song title
      setSongTitle(`${replaceSectionData.title} (Replace Section)`);
      
      // Mark data as used
      onReplaceSectionDataUsed?.();
    }
  }, [replaceSectionData, onReplaceSectionDataUsed]);

  // Handle reuse lyrics data
  useEffect(() => {
    if (reuseLyricsData) {
      // Reset all form data and set up reuse lyrics mode in one batch
      setShowCoverSong(false); // No audio player for reuse lyrics
      setSelectedAudioFile(null);
      setTrimmedAudioData(null);
      setIsExtendMode(false);
      setIsReplaceSectionMode(false);
      setIsIntroMode(false);
      setIsCoverMode(false);
      setIsFromWorkspace(false); // Not from workspace audio action
      
      // Populate lyrics with generic content based on the song's genre
      setLyrics(`[Verse 1]
I'm reaching for the stars tonight
Dancing in the neon light
Every moment feels so right
When you're here beside me

[Chorus]
We're living in this moment now
Nothing else can bring us down
Turn the music up so loud
Let the world just fade away

[Verse 2]
Colors painting through the sky
Time just seems to pass us by
Never need to question why
This feeling's getting stronger

[Bridge]
Every heartbeat synchronize
With the rhythm in our eyes
This is how we come alive
Nothing else can touch us now

[Chorus]
We're living in this moment now
Nothing else can bring us down
Turn the music up so loud
Let the world just fade away`);
      
      // Keep song description empty for reuse lyrics (no Custom mode)
      setSongDescription(reuseLyricsData.genres);
      
      // Clear selected genres for suggestion display
      setSelectedGenres([]);
      
      // Set song title with reuse lyrics indication
      setSongTitle(`${reuseLyricsData.title} (Reuse Lyrics)`);
      
      // No audio player needed for reuse lyrics - keep showCoverSong false
      
      // Mark data as used
      onReuseLyricsDataUsed?.();
    }
  }, [reuseLyricsData, onReuseLyricsDataUsed]);

  // Handle intro song data
  useEffect(() => {
    if (introSongData) {
      // Create a mock audio file URL for the cover song
      const mockAudioUrl = "/audio.mp3"; // You could use a real URL based on the song data
      
      // Reset all form data and set up intro mode in one batch
      setShowCoverSong(true);
      setSelectedAudioFile(null);
      setIsExtendMode(false);
      setIsReplaceSectionMode(false);
      setIsIntroMode(true);
      setIsCoverMode(false);
      setIsFromWorkspace(true);
      onModeChange?.("Custom");
      setTrimmedAudioData({
        fileName: introSongData.title,
        audioUrl: mockAudioUrl,
        image: introSongData.image,
        trimStart: 0,
        trimEnd: 1,
        originalDuration: parseFloat(introSongData.duration.replace(':', '.'))
      });
      
      // Leave lyrics empty for intro mode
      setLyrics('');
      
      // Populate style description with the original song's genres for Custom mode
      setStyleDescription(introSongData.genres);
      
      // Clear selected genres for suggestion display
      setSelectedGenres([]);
      
      // Set song title
      setSongTitle(`${introSongData.title} (Intro)`);
      
      // Mark data as used
      onIntroSongDataUsed?.();
    }
  }, [introSongData, onIntroSongDataUsed]);

  // Handle speed song data
  useEffect(() => {
    if (speedSongData) {
      // Create a mock audio file URL for the cover song
      const mockAudioUrl = "/audio.mp3"; // You could use a real URL based on the song data
      
      // Reset all form data and set up speed mode in one batch
      setShowCoverSong(true);
      setSelectedAudioFile(null);
      setIsExtendMode(false);
      setIsReplaceSectionMode(false);
      setIsIntroMode(false);
      setIsCoverMode(false);
      setIsFromWorkspace(true);
      onModeChange?.("Custom");
      setTrimmedAudioData({
        fileName: speedSongData.title,
        audioUrl: mockAudioUrl,
        image: speedSongData.image,
        trimStart: 0,
        trimEnd: 1,
        originalDuration: parseFloat(speedSongData.duration.replace(':', '.'))
      });
      
      // Populate lyrics with generic content
      setLyrics(`[Verse 1]
I'm reaching for the stars tonight
Dancing in the neon light
Every moment feels so right
When you're here beside me

[Chorus]
We're living in this moment now
Nothing else can bring us down
Turn the music up so loud
Let the world just fade away

[Verse 2]
Colors painting through the sky
Time just seems to pass us by
Never need to question why
This feeling's getting stronger`);
      
      // Populate style description with the original song's genres for Custom mode
      setStyleDescription(speedSongData.genres);
      
      // Clear selected genres for suggestion display
      setSelectedGenres([]);
      
      // Set song title
      setSongTitle(`${speedSongData.title} (Speed)`);
      
      // Activate speed mode from workspace
      setIsSpeedModeFromWorkspace(true);
      
      // Mark data as used
      onSpeedSongDataUsed?.();
    }
  }, [speedSongData, onSpeedSongDataUsed]);

  // Handle home to create data
  useEffect(() => {
    if (homeToCreateData) {
      // Set mode first based on homeToCreateData
      const modeToSet = homeToCreateData.mode || "Simple";
      onModeChange?.(modeToSet);
      
      // Reset form state
      setShowCoverSong(false);
      setSelectedAudioFile(null);
      setTrimmedAudioData(null);
      setIsExtendMode(false);
      setIsReplaceSectionMode(false);
      setIsIntroMode(false);
      setIsCoverMode(false);
      setIsFromWorkspace(false);
      setIsMumbleMode(false);
      setIsSpeedMode(false);
      setIsSpeedModeFromWorkspace(false);
      
      // Populate fields based on mode
      if (modeToSet === "Custom") {
        // For Custom mode, populate style description
        setStyleDescription(homeToCreateData.description || "");
        setSongTitle(homeToCreateData.title || "");
        setLyrics("");
        setSongDescription("");
        setSelectedGenres([]);
        setHasAudio(false);
        setHasLyrics(false);
        setIsInstrumental(false);
      } else {
        // For Simple mode, populate song description fields
        setSongDescription(homeToCreateData.description || "");
        setSelectedGenres(homeToCreateData.genres || []);
        setSongTitle(homeToCreateData.title || "");
        setIsInstrumental(homeToCreateData.isInstrumental || false);
        
        // Clear other form fields not used in Simple mode
        setStyleDescription("");
        setLyrics("");
        setHasAudio(false);
        setHasLyrics(false);
      }
      
      // Mark data as used
      onHomeToCreateDataUsed?.();
    }
  }, [homeToCreateData, onHomeToCreateDataUsed, onModeChange]);
  const handleDescriptionChange = (newDescription) => {
    setSongDescription(newDescription);
    setIsSongDescriptionSaved(false);
  };

  const handleGenreAdd = (genre) => {
    if (!selectedGenres.includes(genre)) {
      setSelectedGenres([...selectedGenres, genre]);
      setIsSongDescriptionSaved(false);
    }
  };

  const handleGenreRemove = (genre) => {
    setSelectedGenres(selectedGenres.filter(g => g !== genre));
    setIsSongDescriptionSaved(false);
  };

  const handleSongDescriptionSave = (title) => {
    const newSavedDescription = {
      title: title,
      description: songDescription,
      genres: selectedGenres,
      timestamp: Date.now()
    };
    setSavedDescriptions(prev => [...prev, newSavedDescription]);
    setIsSongDescriptionSaved(true);
  };

  const handleSavedDescriptionSelect = (savedItem) => {
    setSongDescription(savedItem.description);
    setSelectedGenres(savedItem.genres);
    setIsSongDescriptionSaved(true);
  };

  const handleStyleDescriptionChange = (newDescription) => {
    setStyleDescription(newDescription);
    setIsStyleDescriptionSaved(false);
  };

  const handleStyleGenreAdd = (genre) => {
    if (!styleGenres.includes(genre)) {
      setStyleGenres([...styleGenres, genre]);
      setIsStyleDescriptionSaved(false);
    }
  };

  const handleStyleGenreRemove = (genre) => {
    setStyleGenres(styleGenres.filter(g => g !== genre));
    setIsStyleDescriptionSaved(false);
  };

  const handleStyleDescriptionSave = (title) => {
    const newSavedDescription = {
      title: title,
      description: styleDescription,
      genres: styleGenres,
      timestamp: Date.now()
    };
    setSavedStyleDescriptions(prev => [...prev, newSavedDescription]);
    setIsStyleDescriptionSaved(true);
  };

  const handleSavedStyleDescriptionSelect = (savedItem) => {
    setStyleDescription(savedItem.description);
    setStyleGenres(savedItem.genres);
    setIsStyleDescriptionSaved(true);
  };

  const handleStyleDescriptionUndo = () => {
    setStyleDescription("");
    setIsStyleDescriptionSaved(false);
  };

  const handleStyleDescriptionClear = () => {
    setStyleDescription("");
    setStyleGenres([]);
    setIsStyleDescriptionSaved(false);
  };

  const handleAudioClick = () => {
    // Create file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setSelectedAudioFile(file);
        setShowTrimModal(true);
      }
    };
    input.click();
  };

  const handlePersonaClick = () => {
    // TODO: Implement persona functionality
    console.log('Persona clicked');
  };

  const handleTrimContinue = (trimData) => {
    // Create a new blob URL that won't be revoked by the modal
    let finalAudioUrl = trimData.audioUrl;
    if (selectedAudioFile) {
      // Create a new blob URL for the audio player
      finalAudioUrl = URL.createObjectURL(selectedAudioFile);
    }
    
    // Store the trimmed audio data with the new URL
    const finalTrimData = {
      ...trimData,
      audioUrl: finalAudioUrl
    };
    setTrimmedAudioData(finalTrimData);
    
    // Set the uploading file name
    setUploadingFileName(trimData.fileName || selectedAudioFile?.name || "Untitled");
    // Record when upload starts for progress tracking
    uploadStartTimeRef.current = Date.now();
    // Reset upload states to ensure fresh animation
    setAudioUploadState("normal");
    setAudioUploadProgress(0);
    setAudioUploadRemainingTime(0);
    // Close trim modal and show uploading modal
    setShowTrimModal(false);
    setShowUploadingModal(true);
    console.log('Audio trimmed and continued', finalTrimData);
  };

  const handleTrimClose = () => {
    setShowTrimModal(false);
    setSelectedAudioFile(null);
  };

  const handleSongTitleChange = (newTitle) => {
    setSongTitle(newTitle);
  };

  const handleWorkspaceClick = () => {
    // TODO: Implement workspace functionality
    console.log('Workspace clicked');
  };

  const handleClearAll = () => {
    // Revoke blob URL to prevent memory leaks
    if (trimmedAudioData?.audioUrl && trimmedAudioData.audioUrl.startsWith('blob:')) {
      URL.revokeObjectURL(trimmedAudioData.audioUrl);
    }
    
    // Clear all data
    setSongTitle("");
    setSongDescription("");
    setStyleDescription("");
    setSelectedGenres([]);
    setStyleGenres([]);
    setIsSongDescriptionSaved(false);
    setIsStyleDescriptionSaved(false);
    setLyrics("");
    setShowCoverSong(false);
    setSelectedAudioFile(null);
    setTrimmedAudioData(null);
    setIsExtendMode(false);
    setIsReplaceSectionMode(false);
    setIsIntroMode(false);
    setIsCoverMode(false);
    setIsSpeedMode(false);
    setIsSpeedModeFromWorkspace(false);
    setIsKaraokeModeFromWorkspace(false);
    setIsFromWorkspace(false);
    setShowUploadingModal(false);
    setUploadingFileName("");
    setSelectedUploadOption("Cover");
    setAudioUploadState("normal");
    setAudioUploadProgress(0);
    setAudioUploadRemainingTime(0);
    uploadStartTimeRef.current = null;
    console.log('Clear all clicked');
  };

  const handleCreateSong = () => {
    const finalTitle = songTitle.trim() || "Untitled";
    const songData = {
      title: finalTitle,
      description: styleDescription,
      genres: styleDescription || styleGenres, // Use styleDescription (text) first, fallback to styleGenres array
      mode: selectedMode,
      version: selectedVersion,
      lyrics: lyrics,
      hasAudio: showCoverSong
    };
    console.log('Create song clicked');
    console.log(songData);
    onCreateSong?.(songData);
  };

  const handleSongDescriptionUndo = () => {
    // Simple undo - could be enhanced with history
    setSongDescription("");
    setIsSongDescriptionSaved(false);
  };

  const handleSongDescriptionClear = () => {
    setSongDescription("");
    setSelectedGenres([]);
    setIsSongDescriptionSaved(false);
  };

  const handleMumbleModeChange = (isEnabled) => {
    setIsMumbleMode(isEnabled);
  };

  const handleGenreToggle = (genre) => {
    setSelectedGenres(prev => 
      prev.includes(genre) 
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const handleSimpleAudioClick = () => {
    setHasAudio(!hasAudio);
  };

  const handleSimpleLyricsClick = () => {
    onModeChange?.("Custom");
  };

  const handleInstrumentalToggle = () => {
    setIsInstrumental(!isInstrumental);
  };

  const handleSpeedModeChange = (isActive) => {
    setIsSpeedMode(isActive);
    // Also reset workspace speed mode when manually changing
    if (!isActive) {
      setIsSpeedModeFromWorkspace(false);
    }
  };

  const handleExtendModeChange = (isActive) => {
    setIsExtendMode(isActive);
  };

  const handleReplaceModeChange = (isActive) => {
    setIsReplaceSectionMode(isActive);
  };

  const handleCoverModeChange = (isActive) => {
    // Reset all other modes when Cover is selected
    if (isActive) {
      setIsExtendMode(false);
      setIsReplaceSectionMode(false);
      setIsIntroMode(false);
      setIsCoverMode(true);
      setIsSpeedMode(false);
      setIsSpeedModeFromWorkspace(false);
    }
  };

  const handleIntroLengthChange = (length) => {
    setIntroLength(length);
  };

  const handleIntroModeChange = (isActive) => {
    setIsIntroMode(isActive);
    if (isActive) {
      // Clear lyrics when entering intro mode
      setLyrics('');
    }
  };

  const handleUploadingCancel = () => {
    setShowUploadingModal(false);
    setTrimmedAudioData(null);
    setSelectedAudioFile(null);
    setUploadingFileName("");
    setSelectedUploadOption("Cover");
    // Reset upload states for next time
    setAudioUploadState("normal");
    setAudioUploadProgress(0);
    setAudioUploadRemainingTime(0);
    uploadStartTimeRef.current = null;
  };

  const handleUploadingContinue = (selectedOption) => {
    // Close uploading modal and show audio player in normal state
    setShowUploadingModal(false);
    setShowCoverSong(true);
    setAudioUploadState("normal");
    setAudioUploadProgress(100);
    
    // Set the appropriate mode immediately - map uploading modal options to internal modes
    setIsExtendMode(selectedOption === "Extend");
    setIsReplaceSectionMode(selectedOption === "Replace");
    setIsIntroMode(selectedOption === "Intro");
    setIsSpeedModeFromWorkspace(selectedOption === "Speed");
    
    // Keep isFromWorkspace as false - this is regular creation mode, not workspace remix mode
    
    // Clear lyrics if intro mode
    if (selectedOption === "Intro") {
      setLyrics('');
    }
    
    console.log('Upload completed with option:', selectedOption);
  };

  const handleUploadOptionSelect = (option) => {
    setSelectedUploadOption(option);
  };

  const handleAudioUploadCancel = () => {
    // Revoke blob URL to prevent memory leaks
    if (trimmedAudioData?.audioUrl && trimmedAudioData.audioUrl.startsWith('blob:')) {
      URL.revokeObjectURL(trimmedAudioData.audioUrl);
    }
    
    setShowCoverSong(false);
    setAudioUploadState("normal");
    setAudioUploadProgress(0);
    setAudioUploadRemainingTime(0);
    setTrimmedAudioData(null);
    setSelectedAudioFile(null);
    setUploadingFileName("");
    setSelectedUploadOption("Cover");
    setIsExtendMode(false);
    setIsReplaceSectionMode(false);
    setIsIntroMode(false);
    setIsCoverMode(false);
    setIsSpeedModeFromWorkspace(false);
    uploadStartTimeRef.current = null;
  };

  return (
    <div className="CreateApp relative flex flex-col h-full w-full">
      {credits === 0 && (
        <div className="sticky top-0 z-40 bg-transparent">
          <ZeroCreditsBanner />
        </div>
      )}
      <div className={`flex-1 overflow-y-auto scrollbar-hide ${selectedMode === "Chat" ? "" : "pb-24"}`}>
        {selectedMode === "Chat" ? (
          <div className="h-full">
            <Chat
              onModeChange={onModeChange}
              onPlaySong={onPlaySong}
              onSelectSong={onSelectSong}
              selectedSongTitle={selectedSongTitle}
              selectedSongForForm={selectedSongForForm}
              messages={chatMessages}
              setMessages={setChatMessages}
              hasMessages={hasMessages}
              setHasMessages={setHasMessages}
              playingSongIndex={playingSongIndex}
              setPlayingSongIndex={setPlayingSongIndex}
              selectedSongIndex={selectedSongIndex}
              setSelectedSongIndex={setSelectedSongIndex}
              onSongSelected={handleSongSelected}
              onCreateWorkspace={onCreateWorkspace}
              onOpenWorkspace={onOpenWorkspace}
              isCreating={isCreating}
              onOpenLyricsEditor={onOpenLyricsEditor}
              onOpenStylesEditor={onOpenStylesEditor}
              onRegisterSendMessage={onRegisterSendMessage}
              selectedVersion={selectedVersion}
              onVersionChange={onVersionChange}
              hasPlaybar={hasPlaybar}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-4 p-4">
            {(!showCoverSong || !isSpeedModeFromWorkspace) && !isKaraokeMode && !isKaraokeModeFromWorkspace && (
              <CustomActions
              onAudioClick={handleAudioClick}
              onPersonaClick={handlePersonaClick}
              showAudioButton={!showCoverSong && !selectedSongForForm}
              showPersonaButton={!isSpeedModeFromWorkspace}
            />
          )}
          {showCoverSong && (
            <CreateAudioPlayer
                  key={`audio-player-${karaokeSongData?.id || 'default'}-${isKaraokeModeFromWorkspace}-${selectedSongForForm?.title}`}
                  title={selectedSongForForm?.title || trimmedAudioData?.fileName || selectedAudioFile?.name || "Untitled"}
                  audioSrc={trimmedAudioData?.audioUrl || (selectedAudioFile ? URL.createObjectURL(selectedAudioFile) : "/audio.mp3")}
                  image={selectedSongForForm?.image || trimmedAudioData?.image}
                  onPlay={() => console.log('Play clicked')}
                  onCover={() => console.log('Cover clicked')}
                  onClose={() => {
                    if (selectedSongForForm) {
                      // Deselect the song
                      onSelectSong(null);
                    } else if (isSpeedModeFromWorkspace || isSpeedMode) {
                      // Reset entire form when closing speed mode
                      handleClearAll();
                    } else {
                      // Revoke blob URL to prevent memory leaks
                      if (trimmedAudioData?.audioUrl && trimmedAudioData.audioUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(trimmedAudioData.audioUrl);
                      }
                      setShowCoverSong(false);
                      setSelectedAudioFile(null);
                      setTrimmedAudioData(null);
                      setIsExtendMode(false);
                      setIsReplaceSectionMode(false);
                      setIsIntroMode(false);
                      setIsCoverMode(false);
                      setIsSpeedModeFromWorkspace(false);
                      setIsKaraokeModeFromWorkspace(false);
                      setIsFromWorkspace(false);
                    }
                  }}
                  isExtendMode={isExtendMode}
                  isReplaceSectionMode={isReplaceSectionMode}
                  isIntroMode={isIntroMode}
                  isSpeedMode={isSpeedModeFromWorkspace || isSpeedMode}
                  isFromWorkspace={isFromWorkspace}
                  onSpeedModeChange={handleSpeedModeChange}
                  onIntroLengthChange={handleIntroLengthChange}
                  onIntroModeChange={handleIntroModeChange}
                  onExtendModeChange={handleExtendModeChange}
                  onReplaceModeChange={handleReplaceModeChange}
                  onCoverModeChange={handleCoverModeChange}
                  onKaraokeModeChange={setIsKaraokeMode}
                  initialSelectedOption={(() => {
                    const option = isKaraokeModeFromWorkspace ? "Karaoke" : "Cover";
                    console.log('CreateForm: Passing initialSelectedOption:', option, 'isKaraokeModeFromWorkspace:', isKaraokeModeFromWorkspace);
                    return option;
                  })()}
                  selectedMode={selectedMode}
                  uploadState={audioUploadState}
                  uploadProgress={audioUploadProgress}
                  uploadRemainingTime={audioUploadRemainingTime}
                  onUploadCancel={handleAudioUploadCancel}
                />
          )}
          {!isMumbleMode && !isSpeedMode && !isSpeedModeFromWorkspace && !isKaraokeMode && !isKaraokeModeFromWorkspace && (
            <LyricsComponent
              lyrics={lyrics}
              onLyricsChange={setLyrics}
              isIntroMode={isIntroMode}
              introLength={introLength}
              isCollapsed={isFromWorkspace && (isCoverMode || isReplaceSectionMode)}
            />
          )}
          {!isSpeedMode && !isSpeedModeFromWorkspace && !isKaraokeMode && !isKaraokeModeFromWorkspace && (
            <StyleDescription
              description={styleDescription}
              onDescriptionChange={handleStyleDescriptionChange}
              genres={styleGenres}
              onGenreAdd={handleStyleGenreAdd}
              onGenreRemove={handleStyleGenreRemove}
              onSave={handleStyleDescriptionSave}
              onUndo={handleStyleDescriptionUndo}
              onClear={handleStyleDescriptionClear}
              isSaved={isStyleDescriptionSaved}
              hasSavedDescriptions={savedStyleDescriptions.length > 0}
              savedDescriptions={savedStyleDescriptions}
              onSavedDescriptionSelect={handleSavedStyleDescriptionSelect}
            />
          )}
          {!isSpeedMode && !isSpeedModeFromWorkspace && !isKaraokeMode && !isKaraokeModeFromWorkspace && (
            <AdvancedOptions
              hasAudio={showCoverSong}
              onMumbleModeChange={handleMumbleModeChange}
            />
          )}
          <AdditionalOptions
            songTitle={songTitle}
            onSongTitleChange={handleSongTitleChange}
            onWorkspaceClick={handleWorkspaceClick}
            workspace={(() => {
              if (selectedWorkspaceId) {
                const workspace = workspaces.find(w => w.id === selectedWorkspaceId);
                return workspace ? workspace.title : "New Workspace";
              }
              return "New Workspace";
            })()}
          />
          </div>
        )}
      </div>

      {/* Sticky Footer - Pinned to bottom of CreateForm - Only visible in Custom mode */}
      {selectedMode === "Custom" && (
        <div
          className="absolute left-0 right-0 z-50 flex justify-center"
          style={{ bottom: hasPlaybar ? '84px' : '0' }}
        >
          <Footer
            onClearAll={handleClearAll}
            onCreateSong={handleCreateSong}
            isCreateDisabled={!songTitle && !styleDescription && !lyrics && !showCoverSong}
            showClearButton={!!(lyrics || styleDescription || showCoverSong)}
          />
        </div>
      )}

      {/* Trim Audio Modal */}
      {showTrimModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <TrimAudioModal
            fileName={selectedAudioFile?.name || "Untitled"}
            audioFile={selectedAudioFile}
            onContinue={handleTrimContinue}
            onClose={handleTrimClose}
          />
        </div>
      )}

      {/* Uploading Modal */}
      <UploadingModal
        fileName={uploadingFileName}
        selectedOption={selectedUploadOption}
        onOptionSelect={handleUploadOptionSelect}
        onCancel={handleUploadingCancel}
        onContinue={handleUploadingContinue}
        isVisible={showUploadingModal}
        selectedMode={selectedMode}
      />
    </div>
  );
};

export default CreateForm;